import type {
  DCLicensesUpdateModel,
  DCRentAdjustment,
  DCWaterLeadMap,
  DocumentUpsertModel,
  HomeListingLicensesModel,
  HomeListingLicensesUpdateModel,
} from 'api/models';
import {
  MediaFieldValue,
  toDisplayDate,
  toIso,
  toMediaValue,
} from '../licenses.shared';

// The form stores the wire values verbatim, so no enum translation is needed in
// either direction. Keep these in sync with `DCLicensesUpdateModel`.
export const RAD_STATUS = {
  EXEMPT: 'RegisteredExempt',
  STABILIZED: 'RegisteredRentStabilized',
  NONE: 'NotRegistered',
} as const;

export const EXEMPTION_REASON = {
  SMALL_LANDLORD: 'SmallLandlord',
  NEW_CONSTRUCTION: 'NewConstruction',
  GOVERNMENT: 'GovernmentOwnedOrSubsidized',
  COOPERATIVE: 'CooperativeUnit',
  OTHER: 'Other',
} as const;

/**
 * DC asks about the past 3 years of rent adjustments, but a home may have had any
 * number of them in that window — two in one year, or none at all in a year without
 * an increase — so the only thing we can require of a rent-stabilized home is one.
 *
 * Doubles as the number of blank rows seeded on screen: every field in a row is
 * `required`, so seeding more than the minimum would make the extra rows look
 * mandatory. Further adjustments are added with the "+ Add Adjustment" button.
 */
export const MIN_RENT_HISTORY_ROWS = 1;

// DC lead water-service-line disclosure (DC Water leadmap) applies to homes
// built before 1986.
const LEAD_WATER_YEAR_CUTOFF = 1986;

export function isPre1986(year: unknown): boolean {
  const parsed = Number(year);
  return parsed > 0 && parsed < LEAD_WATER_YEAR_CUTOFF;
}

interface RentHistoryFormRow {
  year?: string | number;
  priorRent?: string | number;
  newRent?: string | number;
  effectiveDate?: string;
  basis?: DCRentAdjustment['basis'];
  comparableUnit?: string;
}

/**
 * Form state for the DC Licenses step. Field names mirror `DCLicensesUpdateModel`
 * so the mapping below only has to add the market envelope and convert dates,
 * numbers, and uploaded files.
 *
 * Declared as a `type` rather than an `interface` so it keeps the implicit index
 * signature that `StepFormLayout`'s `initialValues: Record<string, unknown>`
 * requires.
 */
export type LicensesFormValues = {
  yearBuilt?: string | number;
  radStatus?: DCLicensesUpdateModel['radStatus'];
  radExemptionNumber?: string;
  exemptionReason?: DCLicensesUpdateModel['exemptionReason'];
  radRegistrationNumber?: string;
  radForm1File?: MediaFieldValue;
  basicBusinessLicense?: {
    number?: string;
    nameOnLicense?: string;
    expirationDate?: string;
    file?: MediaFieldValue;
  };
  certificateOfOccupancy?: {
    number?: string;
    issuanceDate?: string;
    file?: MediaFieldValue;
  };
  dcWaterLeadMap?: DCWaterLeadMap;
  rentHistory?: RentHistoryFormRow[];
};

/** `unmaskNumber`/number inputs hand back digit strings; the API wants numbers. */
function toNumber(value: unknown): number | undefined {
  if (value === null || value === undefined || value === '') return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

/**
 * The backend's own 2+-unit signal — preferred over deriving it client-side from
 * the property type, which is not yet settled on first render.
 */
export function isCertificateOfOccupancyRequired(
  currentStepFormData?: HomeListingLicensesModel | null,
): boolean {
  return Boolean(currentStepFormData?.dc?.certificateOfOccupancyRequired);
}

/**
 * `StandardFieldArray` renders its "+ Add" button only on an existing row, so an
 * empty array would be un-addable — seeding `MIN_RENT_HISTORY_ROWS` blank rows keeps
 * the button reachable without implying more adjustments are owed than there are.
 */
function toRentHistoryRows(
  rentHistory?: DCRentAdjustment[],
): RentHistoryFormRow[] {
  const rows: RentHistoryFormRow[] = (rentHistory ?? []).map((row) => ({
    ...row,
    effectiveDate: toDisplayDate(row.effectiveDate),
  }));

  while (rows.length < MIN_RENT_HISTORY_ROWS) {
    rows.push({});
  }

  return rows;
}

/**
 * A row the homeowner has actually started filling in. The seeded blanks are real
 * array entries, so both the step's gate and the payload have to tell them apart
 * from disclosed adjustments.
 */
export function hasRentAdjustmentData(row?: RentHistoryFormRow): boolean {
  return Boolean(
    row &&
    Object.values(row).some(
      (value) => value !== undefined && value !== null && value !== '',
    ),
  );
}

/** Maps `GET …/unit/licenses/{stepId}` onto form state. */
export function toLicensesFormValues(
  currentStepFormData?: HomeListingLicensesModel | null,
): LicensesFormValues {
  const dc = currentStepFormData?.dc;
  if (!dc) return { rentHistory: toRentHistoryRows() };

  const values: LicensesFormValues = {
    yearBuilt: dc.yearBuilt,
    radStatus: dc.radStatus,
    radExemptionNumber: dc.radExemptionNumber,
    exemptionReason: dc.exemptionReason,
    radRegistrationNumber: dc.radRegistrationNumber,
    dcWaterLeadMap: dc.dcWaterLeadMap,
    basicBusinessLicense: {
      number: dc.basicBusinessLicense?.number,
      nameOnLicense: dc.basicBusinessLicense?.nameOnLicense,
      expirationDate: toDisplayDate(dc.basicBusinessLicense?.expirationDate),
      file: toMediaValue(dc.basicBusinessLicense?.file),
    },
    certificateOfOccupancy: {
      number: dc.certificateOfOccupancy?.number,
      issuanceDate: toDisplayDate(dc.certificateOfOccupancy?.issuanceDate),
      file: toMediaValue(dc.certificateOfOccupancy?.file),
    },
    rentHistory: toRentHistoryRows(dc.rentHistory),
  };

  const radForm1File = toMediaValue(dc.radForm1File);
  if (radForm1File) values.radForm1File = radForm1File;

  return values;
}

function toRentAdjustment(row: RentHistoryFormRow): DCRentAdjustment {
  return {
    year: toNumber(row.year),
    priorRent: toNumber(row.priorRent),
    newRent: toNumber(row.newRent),
    effectiveDate: toIso(row.effectiveDate),
    basis: row.basis,
    // Only meaningful for the 213(a)(2) comparable-vacancy basis; drop any value
    // left behind after switching the basis away from it.
    comparableUnit:
      row.basis === 'ComparableUnitVacancy213a2'
        ? row.comparableUnit
        : undefined,
  };
}

interface LicensesUploadedFiles {
  certificateOfOccupancyRequired?: boolean;
  radForm1File?: DocumentUpsertModel;
  basicBusinessLicenseFile?: DocumentUpsertModel;
  certificateOfOccupancyFile?: DocumentUpsertModel;
}

/**
 * Builds the `PUT …/unit/licenses/{stepId}` body. The market envelope is
 * required: `HomeListingLicensesUpdateModel` carries nothing but `dc`, so a flat
 * payload is discarded wholesale by the backend.
 *
 * The `HomeListingLicensesUpdateModel` return type is load-bearing — it is what
 * keeps every field name and enum value here checked against the generated
 * client instead of failing silently at runtime.
 */
export function toLicensesUpdatePayload(
  values: LicensesFormValues,
  {
    certificateOfOccupancyRequired,
    radForm1File,
    basicBusinessLicenseFile,
    certificateOfOccupancyFile,
  }: LicensesUploadedFiles,
): HomeListingLicensesUpdateModel {
  const { radStatus } = values;
  const isExempt = radStatus === RAD_STATUS.EXEMPT;
  const isStabilized = radStatus === RAD_STATUS.STABILIZED;

  const dc: DCLicensesUpdateModel = {
    radStatus,
    yearBuilt: toNumber(values.yearBuilt),
    basicBusinessLicense: {
      number: values.basicBusinessLicense?.number,
      nameOnLicense: values.basicBusinessLicense?.nameOnLicense,
      expirationDate: toIso(values.basicBusinessLicense?.expirationDate),
      file: basicBusinessLicenseFile,
    },
  };

  if (isExempt) {
    dc.radExemptionNumber = values.radExemptionNumber;
    dc.exemptionReason = values.exemptionReason;
  }

  if (isStabilized) {
    dc.radRegistrationNumber = values.radRegistrationNumber;
    // Blank seeded rows are dropped rather than sent as empty adjustments — they
    // would otherwise print as empty lines on RAD Form 4.
    dc.rentHistory = (values.rentHistory ?? [])
      .filter(hasRentAdjustmentData)
      .map(toRentAdjustment);
  }

  if (isExempt || isStabilized) {
    dc.radForm1File = radForm1File;
  }

  if (certificateOfOccupancyRequired) {
    dc.certificateOfOccupancy = {
      number: values.certificateOfOccupancy?.number,
      issuanceDate: toIso(values.certificateOfOccupancy?.issuanceDate),
      file: certificateOfOccupancyFile,
    };
  }

  if (isPre1986(values.yearBuilt)) {
    dc.dcWaterLeadMap = values.dcWaterLeadMap;
  }

  return { dc };
}
